import React, {
    useMemo,
    useState,
    useEffect,
    useContext
} from "react";
import { Spin } from "antd";
import { AuthContext } from "../context/AuthContext";
import axios from "axios";
import Tree from "react-d3-tree";

export default function OrgChart({ hodId }) {
    const [orgList, setOrgList] = useState([]);
    const [loading, setLoading] = useState(true);
    const { authData, backUrl } = useContext(AuthContext);
    const [empId, setEmpId] = useState(null);

    const fetchChildrenRecursive = async (list, baseUrl) => {
        const existingIds = new Set(list.map((e) => e.id));
        const toProcess = [...list];

        while (toProcess.length > 0) {
            const emp = toProcess.shift();
            try {
                const res = await axios.get(
                    `${baseUrl}/api/emp/employees/?reporting_to=${emp.id}`,
                    {
                        headers: {
                            Authorization: `Bearer ${authData?.access}`,
                        },
                    }
                );
                const subs = res.data?.results || [];
                for (const s of subs) {
                    if (!existingIds.has(s.id)) {
                        existingIds.add(s.id);
                        list.push(s);
                        toProcess.push(s);
                    }
                }
            } catch (err) {
                console.error("Child fetch error:", emp.id, err);
            }
        }
    };

    const fetchFullTree = async () => {
        try {
            if (!empId) {
                setLoading(false);
                return;
            }

            const you = (
                await axios.get(`${backUrl}/api/emp/employees/${empId}`, {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                    },
                })
            ).data;

            let head = null;
            if (you?.reporting_to) {
                head = (
                    await axios.get(
                        `${backUrl}/api/emp/employees/${you.reporting_to}`,
                        {
                            headers: {
                                Authorization: `Bearer ${authData?.access}`,
                            },
                        }
                    )
                ).data;
            }

            const teamRes = await axios.get(
                `${backUrl}/api/emp/employees/?reporting_to=${empId}`,
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                    },
                }
            );
            const team = teamRes.data?.results || [];

            let orgTemp = [];
            if (head) orgTemp.push(head);
            orgTemp.push(you);
            orgTemp.push(...team);

            await fetchChildrenRecursive(orgTemp, backUrl);
            setOrgList(orgTemp);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const buildTree = (list) => {
        const map = {};
        const roots = [];

        list.forEach((emp) => {
            map[emp.id] = {
                id: emp.id,
                name:
                    `${emp.first_name ?? ""} ${emp.last_name ?? ""}`.trim() || "—",
                is_hod: emp.is_hod,
                designation: emp.designation_name,
                reporting_to: emp.reporting_to,
                image: emp.photo, // IMAGE FIELD
                children: [],
            };
        });

        list.forEach((emp) => {
            if (!emp.reporting_to) {
                roots.push(map[emp.id]);
            } else if (map[emp.reporting_to]) {
                map[emp.reporting_to].children.push(map[emp.id]);
            } else {
                roots.push(map[emp.id]);
            }
        });

        return roots;
    };

    useEffect(() => {
        if (hodId) setEmpId(hodId);
        else setEmpId(authData?.employee?.id);
    }, [hodId]);

    useEffect(() => {
        if (empId) fetchFullTree();
    }, [empId]);

    const treeDataArray = useMemo(() => buildTree(orgList), [orgList]);

    const convertForD3 = (nodes) => {
        if (!nodes || nodes.length === 0) return [];

        return nodes.map((n) => ({
            name: n.name + (n.is_hod ? " (HOD)" : ""),
            image: n.image,
            designation: n.designation,
            is_hod: n.is_hod,
            children: convertForD3(n.children),
        }));
    };

    const prepareRoot = (arr) => {
        const converted = convertForD3(arr);
        if (converted.length === 0) return null;
        if (converted.length === 1) return converted[0];

        return {
            name: "Organization",
            children: converted,
        };
    };

    const root = useMemo(() => prepareRoot(treeDataArray), [treeDataArray]);

    // ---------------------- CUSTOM NODE (WITH IMAGE) ----------------------
    const renderNode = ({ nodeDatum }) => (
        <g>
            {/* IMAGE */}
            <image
                href={nodeDatum.image || "/default_user.png"}
                x={-25}
                y={-25}
                width={50}
                height={50}
                style={{ clipPath: "circle(25px at 25px 25px)" }}
            />

            {/* NAME */}
            <text x="35" y="-5" fontSize="14">
                {nodeDatum.name}
            </text>

            {/* DESIGNATION */}
            <text x="35" y="15" fontSize="12">
                {nodeDatum.designation}
            </text>
        </g>
    );

    // ----------------------------------------------------------------------

    return (
        <div style={{ width: "100%", height: 600 }}>
            {loading ? (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        paddingTop: 80,
                    }}
                >
                    <Spin size="large" />
                </div>
            ) : root ? (
                <Tree
                    data={root}
                    translate={{ x: 600, y: 50 }}
                    orientation="vertical"
                    collapsible={true}
                    zoomable={true}
                    draggable={true}
                    enableLegacyTransitions={true}
                    pathFunc="diagonal"
                    transitionDuration={500}
                    separation={{ siblings: 2, nonSiblings: 2 }}
                    renderCustomNodeElement={renderNode} // IMAGE ENABLED
                />
            ) : (
                <div style={{ padding: 20, textAlign: "center" }}>
                    No data to display
                </div>
            )}
        </div>
    );
}
